import Modal from "react-bootstrap/Modal";
import { THEME_COLOR } from "../../config/club";

export default function PlayerMatchHistoryModal({
    show,
    onClose,
    player,
    matchHistory,
    levelColor,
}) {
    if (!player) return null;

    const matches = matchHistory
        .filter(m => m.match?.some(p => p?.name === player.name))
        .sort((a, b) => {
            const tA = a.startAt?.toMillis?.() ?? 0;
            const tB = b.startAt?.toMillis?.() ?? 0;
            return tB - tA;
        });

    // Helper to determine Win/Loss/Draw
    const getMatchResult = (m) => {
        if (!m.score || m.score === "PENDING") return { text: "PENDING", color: "#6c757d" };

        const [scoreA, scoreB] = m.score.split('-').map(Number);
        const playerOnTeamA = [m.match[0]?.name, m.match[1]?.name].includes(player.name);

        if (scoreA === scoreB) return { text: "DRAW", color: "#6c757d" };

        const teamAWins = scoreA > scoreB;
        const playerWon = (playerOnTeamA && teamAWins) || (!playerOnTeamA && !teamAWins);

        return playerWon
            ? { text: "WIN", color: "#198754" }
            : { text: "LOSS", color: "#dc3545" };
    };

    const renderPlayer = (p, idx, team) => {
        if (!p) return null;
        const isTarget = p.name === player.name;

        return (
            <div
                key={`${team}-${p.id || p.name}-${idx}`}
                className={`d-flex align-items-center gap-2 p-2 mb-1 rounded-3 ${isTarget ? "border border-2" : ""}`}
                style={{
                    backgroundColor: isTarget ? `${THEME_COLOR}10` : "#fcfcfc",
                    borderColor: isTarget ? THEME_COLOR : "transparent",
                    height: "42px" // Keep row heights uniform
                }}
            >
                <span
                    className="badge rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                        backgroundColor: levelColor(p.level),
                        width: 22,
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        color: "#333"
                    }}
                >
                    {p.level ?? 0}
                </span>

                <span
                    className={`text-truncate ${isTarget ? "fw-black" : "fw-bold"}`}
                    style={{
                        color: isTarget ? THEME_COLOR : "#444",
                        fontSize: "0.9rem"
                    }}
                >
                    {p.name}
                </span>

                {isTarget && (
                    <i
                        className="bi bi-star-fill ms-auto text-warning"
                        style={{ fontSize: "0.75rem" }}
                    />
                )}
            </div>
        );
    };
    const summary = matches.reduce(
        (acc, m) => {
            const r = getMatchResult(m);
            acc.total++;
            if (r.text === "WIN") acc.wins++;
            if (r.text === "LOSS") acc.losses++;
            if (r.text === "DRAW") acc.draws++;
            acc.lastPlayed = Math.max(acc.lastPlayed || 0, m.endTime || 0);
            return acc;
        },
        { total: 0, wins: 0, losses: 0, draws: 0, lastPlayed: null }
    );

    const winRate = summary.total
        ? Math.round((summary.wins / summary.total) * 100)
        : 0;

    return (
        <Modal
            show={show}
            onHide={onClose}
            size="lg" // Changed to LG for better vertical proportion
            centered
            scrollable
            contentClassName="border-0 shadow-lg rounded-5"
        >
            <Modal.Header closeButton className="border-0 px-4 pt-4">
                <Modal.Title className="fw-black text-uppercase">
                    <i className="bi bi-person-badge me-2" style={{ color: THEME_COLOR }} />
                    {player.name}
                    <div className="fw-bold text-muted mt-1" style={{ fontSize: "0.75rem", letterSpacing: 1 }}>
                        PLAYER MATCH HISTORY
                    </div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 pb-4">
                {/* SUMMARY STRIP */}
                <div className="mb-4 p-3 rounded-4 border bg-light">
                    <div className="row text-center g-3">
                        <div className="col">
                            <div className="fw-black fs-5">{summary.total}</div>
                            <div className="text-muted small">Matches</div>
                        </div>
                        <div className="col">
                            <div className="fw-black fs-5 text-success">{summary.wins}</div>
                            <div className="text-muted small">Wins</div>
                        </div>
                        <div className="col">
                            <div className="fw-black fs-5 text-danger">{summary.losses}</div>
                            <div className="text-muted small">Losses</div>
                        </div>
                        {summary?.draws !== undefined && summary?.draws !== null && summary?.draws !== 0 && (
                            <div className="col">
                                <div className="fw-black fs-5 text-secondary">{summary.draws}</div>
                                <div className="text-muted small">Draw</div>
                            </div>
                        )}

                        <div className="col">
                            <div className="fw-black fs-5">{winRate}%</div>
                            <div className="text-muted small">Win Rate</div>
                        </div>
                    </div>
                </div>

                {matches.length === 0 ? (
                    <div className="text-center py-5 text-muted fw-bold">
                        No matches played yet
                    </div>
                ) : (
                    matches.map((m, i) => {
                        const teamA = [m.match[0], m.match[1]];
                        const teamB = [m.match[2], m.match[3]];
                        const score = m.score || "0-0";
                        const result = getMatchResult(m);
                        const endedDate = m.endTime ? new Date(m.endTime) : (m.startAt?.toDate ? m.startAt.toDate() : null);

                        return (
                            <div
                                key={m.id + i}
                                className="mb-2 px-3 py-2 bg-white border rounded-3 shadow-sm"
                            >
                                {/* Top Line: Result + Date */}
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <div className="d-flex align-items-center gap-2">
                                         <span className="text-muted" style={{ fontSize: "0.6rem" }}>
                                            #{i + 1}
                                        </span>
                                        <span
                                            className="badge fw-black text-uppercase"
                                            style={{
                                                backgroundColor: `${result.color}20`,
                                                color: result.color,
                                                fontSize: "0.6rem",
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {result.text}
                                        </span>

                                       
                                    </div>

                                    <span className="text-muted" style={{ fontSize: "0.6rem" }}>
                                        {endedDate
                                            ? `${endedDate.toLocaleString("en-US", {
                                                dateStyle: "medium",
                                            })} · ${endedDate.toLocaleTimeString([], {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}`
                                            : "—"}
                                    </span>
                                </div>

                                {/* Main Row */}
                                <div className="row align-items-center g-1">
                                    {/* Team A */}
                                    <div className="col-5">
                                        {teamA.map((p, idx) => renderPlayer(p, idx, "A"))}
                                    </div>

                                    {/* Score */}
                                    <div className="col-2 text-center">
                                        <div
                                            className="fw-black"
                                            style={{
                                                fontSize: "1.2rem",
                                                color: result.color,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {score}
                                        </div>
                                    </div>

                                    {/* Team B */}
                                    <div className="col-5 text-end">
                                        {teamB.map((p, idx) => renderPlayer(p, idx, "B"))}
                                    </div>
                                </div>
                            </div>

                        );
                    })
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 px-4 pb-4">
                <button
                    className="btn rounded-pill px-5 fw-black text-uppercase shadow-sm"
                    style={{ backgroundColor: THEME_COLOR, color: "#fff", fontSize: '0.8rem' }}
                    onClick={onClose}
                >
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
}